import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Box, 
  useTheme, 
  Button, 
  Card, 
  CardContent, 
  Typography, 
  Divider,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  Snackbar,
  Paper,
} from '@mui/material';
import { tokens } from '../../theme';
import Headeer from '../../components/Headeer';
import ConfirmationDialog from '../../components/ConfirmationDialog';
import { useParams, useNavigate } from 'react-router-dom';
import FormulaService from '../../services/doctorServices/FormulaService';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import FunctionsIcon from '@mui/icons-material/Functions';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import DescriptionIcon from '@mui/icons-material/Description';
import CodeIcon from '@mui/icons-material/Code';

const ViewFormula = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { t, i18n } = useTranslation('formulas');
  const navigate = useNavigate();
  const { id } = useParams();
  const formulaService = useMemo(() => new FormulaService(), []);

  const dateLocale = useMemo(() => {
    const lang = i18n.language || "en";
    if (lang.startsWith("tr")) return "tr-TR";
    if (lang.startsWith("fr")) return "fr-FR";
    return "en-US";
  }, [i18n.language]);

  const [formula, setFormula] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const normalizedExpression = useMemo(
    () => (formula?.formula_expression ?? "").trim().replace(/\s+/g, " "),
    [formula?.formula_expression]
  );
  const showPercentHelp = normalizedExpression === "TOTAL_PERCENT";
  const showNormHelp = normalizedExpression === "TOTAL_NORMALIZED";
  /** Hazır şablon: (TOTAL_SCORE / TOTAL_MAX) * 100 — eski * 10 formları da göster */
  const showCustomRangeHelp =
    normalizedExpression === "(TOTAL_SCORE / TOTAL_MAX) * 100" ||
    normalizedExpression === "(TOTAL_SCORE / TOTAL_MAX) * 10";

  useEffect(() => {
    fetchFormula();
  }, [id]);

  const fetchFormula = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await formulaService.getFormula(id);
      setFormula(data);
    } catch (err) {
      console.error('Error loading formula:', err);
      // 403 (Permission) errors are handled by global interceptor
      if (err.response?.status === 403) {
        setTimeout(() => navigate('/formulas'), 2000);
      } else {
        setError(t('errorLoadingFormula'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await formulaService.deleteFormula(id);
      setSnackbar({
        open: true,
        message: t('formulaDeletedSuccessfully'),
        severity: "success"
      });
      setDeleteDialogOpen(false);
      setTimeout(() => {
        navigate('/formulas');
      }, 1500);
    } catch (error) {
      console.error('Error deleting formula:', error);
      setDeleteDialogOpen(false);
      // 403 (Permission) errors are handled by global interceptor
      if (error.response?.status !== 403) {
        const errorMessage = error.response?.data?.message || error.response?.data?.error || t('errorDeletingFormula');
        setSnackbar({
          open: true,
          message: errorMessage,
          severity: "error"
        });
      }
      // For 403 errors, global alert will be shown automatically
    }
  };

  if (loading) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        minHeight="calc(100vh - 100px)"
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error || !formula) {
    return (
      <Box m="20px">
        <Alert severity="error" sx={{ mb: 2 }}>
          {error || t('formulaNotFound')}
        </Alert>
        <Button 
          variant="contained" 
          onClick={() => navigate('/formulas')}
          startIcon={<ArrowBackIcon />}
        >
          {t('backToFormulas')}
        </Button>
      </Box>
    );
  }

  /* Sequelize JSON: createdAt; bazı yanıtlar: created_at */
  const createdRaw = formula.created_at ?? formula.createdAt;
  const createdAtDate =
    createdRaw != null && createdRaw !== "" ? new Date(createdRaw) : null;
  const createdAtLabel =
    createdAtDate && !Number.isNaN(createdAtDate.getTime())
      ? createdAtDate.toLocaleString(dateLocale, {
          day: "2-digit",
          month: "long",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        })
      : "—";

  return (
    <Box m="20px">
      {/* Header with Actions */}
      <Box 
        display="flex" 
        justifyContent="space-between" 
        alignItems="flex-start"
        mb={3}
      >
        <Box>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/formulas')}
            sx={{
              color: colors.grey[100],
              mb: 2,
              '&:hover': {
                backgroundColor: colors.primary[400],
              },
            }}
          >
            {t('back')}
          </Button>
          <Headeer
            title={formula.name}
            subtitle={t('formulaDetails')}
          />
        </Box>
        <Box display="flex" gap={2}>
          <Tooltip title={t('edit')}>
            <Button
              variant="contained"
              startIcon={<EditIcon />}
              onClick={() => navigate(`/formulas/edit/${id}`)}
              sx={{
                backgroundColor: colors.greenAccent[600],
                color: colors.grey[100],
                '&:hover': {
                  backgroundColor: colors.greenAccent[700],
                },
              }}
            >
              {t('edit')}
            </Button>
          </Tooltip>
          <Tooltip title={t('delete')}>
            <Button
              variant="contained"
              startIcon={<DeleteIcon />}
              onClick={handleDeleteClick}
              sx={{
                backgroundColor: colors.redAccent[600],
                color: colors.grey[100],
                '&:hover': {
                  backgroundColor: colors.redAccent[700],
                },
              }}
            >
              {t('delete')}
            </Button>
          </Tooltip>
        </Box>
      </Box>

      {/* Formula Details Card */}
      <Card
        sx={{
          backgroundColor: colors.primary[400],
          boxShadow: `0 4px 24px rgba(0, 0, 0, 0.15)`,
          borderRadius: '12px',
          mb: 3,
        }}
      >
        <CardContent sx={{ p: 4 }}>
          {/* Description */}
          {formula.description && (
            <Box mb={4}>
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <DescriptionIcon sx={{ color: colors.blueAccent[400] }} />
                <Typography 
                  variant="h5" 
                  fontWeight="600" 
                  color={colors.grey[100]}
                >
                  {t('description')}
                </Typography>
              </Box>
              <Typography 
                variant="body1" 
                color={colors.grey[300]}
                sx={{ 
                  pl: 4,
                  lineHeight: 1.8,
                }}
              >
                {formula.description}
              </Typography>
            </Box>
          )}

          <Divider sx={{ my: 3, borderColor: colors.primary[500] }} />

          {/* Formula Expression */}
          <Box mb={4}>
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <CodeIcon sx={{ color: colors.greenAccent[400] }} />
              <Typography 
                variant="h5" 
                fontWeight="600" 
                color={colors.grey[100]}
              >
                {t('formulaExpressionLabel')}
              </Typography>
            </Box>
            <Box
              sx={{
                backgroundColor: colors.primary[600],
                border: `2px solid ${colors.greenAccent[700]}`,
                borderRadius: '8px',
                p: 3,
                ml: 4,
                fontFamily: 'monospace',
                fontSize: '1.1rem',
                color: colors.greenAccent[400],
                overflowX: 'auto',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                boxShadow: `0 2px 12px ${colors.greenAccent[900]}40`,
              }}
            >
              {formula.formula_expression}
            </Box>
          </Box>

          {(showPercentHelp || showNormHelp || showCustomRangeHelp) && (
            <Box sx={{ mt: 3, ml: { xs: 0, md: 1 } }}>
              <Divider sx={{ mb: 3, borderColor: colors.primary[500] }} />
              {showPercentHelp && (
                <Paper
                  elevation={0}
                  sx={{
                    p: 2.5,
                    mb: showNormHelp || showCustomRangeHelp ? 2 : 0,
                    backgroundColor: colors.primary[600],
                    border: `1px solid ${colors.blueAccent[600]}`,
                    borderRadius: 2,
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    fontWeight={700}
                    color={colors.grey[100]}
                    sx={{ mb: 1.5 }}
                  >
                    {t("viewPercentTitle")}
                  </Typography>
                  <Typography variant="body2" color={colors.grey[300]} sx={{ mb: 2, lineHeight: 1.7 }}>
                    {t("viewPercentIntro")}
                  </Typography>
                  <Typography variant="subtitle2" fontWeight={600} color={colors.blueAccent[300]} sx={{ mb: 0.75 }}>
                    {t("viewPercentEx1Title")}
                  </Typography>
                  <Typography variant="body2" color={colors.grey[200]} sx={{ pl: 1, borderLeft: `3px solid ${colors.blueAccent[500]}`, mb: 0.5 }}>
                    {t("viewPercentEx1Line1")}
                  </Typography>
                  <Typography variant="body2" color={colors.grey[200]} sx={{ pl: 1, borderLeft: `3px solid ${colors.blueAccent[500]}`, mb: 0.5 }}>
                    {t("viewPercentEx1Line2")}
                  </Typography>
                  <Typography variant="body2" fontWeight={600} color={colors.greenAccent[400]} sx={{ mb: 2 }}>
                    {t("viewPercentEx1Result")}
                  </Typography>
                  <Typography variant="subtitle2" fontWeight={600} color={colors.blueAccent[300]} sx={{ mb: 0.75 }}>
                    {t("viewPercentEx2Title")}
                  </Typography>
                  <Typography variant="body2" color={colors.grey[200]} sx={{ pl: 1, borderLeft: `3px solid ${colors.blueAccent[500]}`, mb: 0.5 }}>
                    {t("viewPercentEx2Line1")}
                  </Typography>
                  <Typography variant="body2" fontWeight={600} color={colors.greenAccent[400]} sx={{ mb: 1 }}>
                    {t("viewPercentEx2Result")}
                  </Typography>
                  <Typography variant="caption" color={colors.grey[400]} sx={{ display: "block", lineHeight: 1.65 }}>
                    {t("viewPercentEx2Note")}
                  </Typography>
                </Paper>
              )}
              {showNormHelp && (
                <Paper
                  elevation={0}
                  sx={{
                    p: 2.5,
                    mb: showCustomRangeHelp ? 2 : 0,
                    backgroundColor: colors.primary[600],
                    border: `1px solid ${colors.greenAccent[600]}`,
                    borderRadius: 2,
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    fontWeight={700}
                    color={colors.grey[100]}
                    sx={{ mb: 1.5 }}
                  >
                    {t("viewNormTitle")}
                  </Typography>
                  <Typography variant="body2" color={colors.grey[300]} sx={{ mb: 2, lineHeight: 1.7 }}>
                    {t("viewNormIntro")}
                  </Typography>
                  <Typography variant="subtitle2" fontWeight={600} color={colors.greenAccent[400]} sx={{ mb: 0.75 }}>
                    {t("viewNormEx1Title")}
                  </Typography>
                  <Typography variant="body2" color={colors.grey[200]} sx={{ pl: 1, borderLeft: `3px solid ${colors.greenAccent[500]}`, mb: 2 }}>
                    {t("viewNormEx1Line1")}
                  </Typography>
                  <Typography variant="subtitle2" fontWeight={600} color={colors.greenAccent[400]} sx={{ mb: 0.75 }}>
                    {t("viewNormEx2Title")}
                  </Typography>
                  <Typography variant="body2" color={colors.grey[200]} sx={{ pl: 1, borderLeft: `3px solid ${colors.greenAccent[500]}`, mb: 0.5 }}>
                    {t("viewNormEx2Line1")}
                  </Typography>
                  <Typography variant="body2" color={colors.grey[200]} sx={{ pl: 1, borderLeft: `3px solid ${colors.greenAccent[500]}`, mb: 2 }}>
                    {t("viewNormEx2Line2")}
                  </Typography>
                  <Typography variant="subtitle2" fontWeight={600} color={colors.greenAccent[400]} sx={{ mb: 0.75 }}>
                    {t("viewNormEx3Title")}
                  </Typography>
                  <Typography variant="body2" fontWeight={600} color={colors.greenAccent[300]}>
                    {t("viewNormEx3Result")}
                  </Typography>
                </Paper>
              )}
              {showCustomRangeHelp && (
                <Paper
                  elevation={0}
                  sx={{
                    p: 2.5,
                    backgroundColor: colors.primary[600],
                    border: `1px solid ${colors.redAccent[600]}`,
                    borderRadius: 2,
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    fontWeight={700}
                    color={colors.grey[100]}
                    sx={{ mb: 1.5 }}
                  >
                    {t("viewCustomRangeTitle")}
                  </Typography>
                  <Typography variant="body2" color={colors.grey[300]} sx={{ mb: 2, lineHeight: 1.7 }}>
                    {t("viewCustomRangeIntro")}
                  </Typography>
                  <Typography variant="subtitle2" fontWeight={600} color={colors.redAccent[300]} sx={{ mb: 0.75 }}>
                    {t("viewCustomRangeEx1Title")}
                  </Typography>
                  <Typography variant="body2" color={colors.grey[200]} sx={{ pl: 1, borderLeft: `3px solid ${colors.redAccent[500]}`, mb: 0.5 }}>
                    {t("viewCustomRangeEx1Line1")}
                  </Typography>
                  <Typography variant="body2" fontWeight={600} color={colors.greenAccent[400]} sx={{ mb: 2 }}>
                    {t("viewCustomRangeEx1Result")}
                  </Typography>
                  <Typography variant="subtitle2" fontWeight={600} color={colors.redAccent[300]} sx={{ mb: 0.75 }}>
                    {t("viewCustomRangeEx2Title")}
                  </Typography>
                  <Typography variant="body2" color={colors.grey[200]} sx={{ pl: 1, borderLeft: `3px solid ${colors.redAccent[500]}`, mb: 0.5 }}>
                    {t("viewCustomRangeEx2Line1")}
                  </Typography>
                  <Typography variant="body2" fontWeight={600} color={colors.greenAccent[400]} sx={{ mb: 1 }}>
                    {t("viewCustomRangeEx2Result")}
                  </Typography>
                  <Typography variant="caption" color={colors.grey[400]} sx={{ display: "block", lineHeight: 1.65 }}>
                    {t("viewCustomRangeEx2Note")}
                  </Typography>
                </Paper>
              )}
            </Box>
          )}

          <Divider sx={{ my: 3, borderColor: colors.primary[500] }} />

          {/* Created At */}
          <Box>
            <Box display="flex" alignItems="center" gap={1} mb={1}>
              <CalendarTodayIcon sx={{ color: colors.grey[400], fontSize: '20px' }} />
              <Typography 
                variant="h6" 
                fontWeight="500" 
                color={colors.grey[300]}
              >
                {t('createdAt')}
              </Typography>
            </Box>
            <Typography 
              variant="body1" 
              color={colors.grey[400]}
              sx={{ pl: 4 }}
            >
              {createdAtLabel}
            </Typography>
          </Box>
        </CardContent>
      </Card>

      {/* Info Box */}
      <Alert 
        severity="info"
        icon={<FunctionsIcon />}
        sx={{
          backgroundColor: `${colors.blueAccent[800]}40`,
          border: `1px solid ${colors.blueAccent[600]}`,
          color: colors.grey[100],
          '& .MuiAlert-icon': {
            color: colors.blueAccent[400],
          },
        }}
      >
        <Typography variant="body2" dangerouslySetInnerHTML={{ __html: t('formulaNote') }} />
      </Alert>

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        open={deleteDialogOpen}
        title={t('deleteFormulaTitle')}
        description={t('deleteFormulaDescription')}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeleteConfirm}
      />

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{
            width: "100%",
            backgroundColor: snackbar.severity === "success" 
              ? colors.greenAccent[700] 
              : colors.redAccent[700],
            color: "white",
            fontWeight: 600,
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ViewFormula;

